import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Divider,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  IconButton,
} from "@mui/material";
import {
  DragIndicator,
  Reorder,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationOnIcon,
  Language as LanguageIcon,
  LinkedIn as LinkedInIcon,
  DriveEta as DriveEtaIcon,
  ChevronLeft,
  ChevronRight,
} from "@mui/icons-material";
import axios from "axios";

export const resumeMeta = {
  hasPhoto: false,
  columns: 1,
};

const pageSizeMap = {
  A4:        { width: "210mm", height: "297mm" },
  Letter:    { width: "216mm", height: "279mm" },
  Legal:     { width: "216mm", height: "356mm" },
  A3:        { width: "297mm", height: "420mm" },
  Executive: { width: "184mm", height: "267mm" },
};

const pageSizePxMap = {
  A4:        { width: 794,  height: 1123 },
  Letter:    { width: 816,  height: 1056 },
  Legal:     { width: 816,  height: 1344 },
  A3:        { width: 1123, height: 1587 },
  Executive: { width: 696,  height: 1010 },
};

const Resume49 = ({
  theme = "#000000",
  color = "#000000",
  font = "'Times New Roman', Times, serif",
  fontSize = "14px",
  fontStyle = "normal",
  headingSize = "18px",
  sectionSpacing = 24,
  paragraphSpacing = 10,
  lineSpacing = 20,
  topBottomMargin = 28,
  sideMargins = 32,
  lineWeight = 1,
  pageSize = "A4",
  exporting = false,
  enableDragDrop = true,
  formData = {},
}) => {
  const page = pageSizeMap[pageSize] || pageSizeMap.A4;
  const pagePx = pageSizePxMap[pageSize] || pageSizePxMap.A4;

  const globalFontStyle = {
    fontFamily: font,
    fontSize: fontSize,
    fontStyle: fontStyle,
    lineHeight: `${lineSpacing}px`,
    color: "#1a1a1a",
  };

  const sectionTitleStyle = {
    ...globalFontStyle,
    fontWeight: 700,
    fontSize: headingSize,
    letterSpacing: "0.5px",
    mb: 1.5,
    color: color,
    borderBottom: `${lineWeight}px solid ${color}`,
    pb: 0.5,
  };
  // ── States ──────────────────────────────────────────────────────────────
  const [personalData, setPersonalData] = useState({
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    city: "",
    country: "",
    pinCode: "",
    currentPosition: "",
    linkedIn: "",
    website: "",
    drivingLicence: "",
  });

  const [savedSummary, setSavedSummary]               = useState("");
  const [educationEntries, setEducationEntries]       = useState([]);
  const [workExperiences, setWorkExperiences]         = useState([]);
  const [savedSkills, setSavedSkills]                 = useState([]);
  const [savedLanguages, setSavedLanguages]           = useState([]);
  const [savedCertifications, setSavedCertifications] = useState([]);
  const [savedAccomplishments, setSavedAccomplishments] = useState([]);
  const [savedReferences, setSavedReferences]         = useState([]);
  const [savedAdditionalInfo, setSavedAdditionalInfo] = useState([]);
  const [softwareSkills, setSoftwareSkills]           = useState([]);
  const [savedVolunteering, setSavedVolunteering]     = useState([]);
  const [savedInterests, setSavedInterests]           = useState([]);
  const [savedWebsites, setSavedWebsites]             = useState([]);
  const [customSections, setCustomSections]           = useState({});

  // Page-number states
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [pageBreaks, setPageBreaks] = useState([]);
  const contentRef = useRef(null);

  // Section ordering
  const defaultSectionOrder = [
    "summary", "education", "experience", "skills",
    "languages", "certifications", "accomplishments",
    "references", "softwareSkills", "volunteering",
    "interests", "websites", "additionalInfo",
  ];
  const [sectionOrder, setSectionOrder]     = useState(defaultSectionOrder);
  const [dragOverIndex, setDragOverIndex]   = useState(null);
  const [arrangeDialogOpen, setArrangeDialogOpen] = useState(false);

  // ── Load data once on mount ──────────────────────────────────────────────
  useEffect(() => {
    const loadInitialData = async () => {
      const token  = localStorage.getItem("token");
      const userId = localStorage.getItem("userId");

      // Load from localStorage
      const storedPersonal = localStorage.getItem("personalData");
      if (storedPersonal) {
        try { setPersonalData(prev => ({ ...prev, ...JSON.parse(storedPersonal) })); } catch {}
      }

      // Summary
      const summaries = JSON.parse(localStorage.getItem("summaries")) || [];
      if (summaries.length > 0) setSavedSummary(summaries[summaries.length - 1]);

      // Education
      const eduStr = localStorage.getItem("educationEntries");
      if (eduStr) { try { setEducationEntries(JSON.parse(eduStr) || []); } catch {} }

      // Skills
      const skillStr = localStorage.getItem("skills");
      if (skillStr) { try { setSavedSkills(JSON.parse(skillStr) || []); } catch {} }

      // Work Experience
      const workStr = localStorage.getItem("workExperiences");
      if (workStr) { try { setWorkExperiences(JSON.parse(workStr) || []); } catch {} }

      // Languages
      const langStr = localStorage.getItem("languagesList");
      if (langStr) { try { setSavedLanguages(JSON.parse(langStr) || []); } catch {} }

      // Certifications
      const certStr = localStorage.getItem("certificationsList");
      if (certStr) { try { setSavedCertifications(JSON.parse(certStr) || []); } catch {} }

      // Accomplishments
      const accoStr = localStorage.getItem("accomplishmentsList");
      if (accoStr) { try { setSavedAccomplishments(JSON.parse(accoStr) || []); } catch {} }

      // References
      const refStr = localStorage.getItem("referencesList");
      if (refStr) { try { setSavedReferences(JSON.parse(refStr) || []); } catch {} }

      // Additional Info
      const addStr = localStorage.getItem("additionalInfo");
      if (addStr) { try { setSavedAdditionalInfo(JSON.parse(addStr) || []); } catch {} }

      // Software Skills
      const swStr = localStorage.getItem("softwareSkills");
      if (swStr) { try { setSoftwareSkills(JSON.parse(swStr) || []); } catch {} }

      // Volunteering
      const volStr = localStorage.getItem("volunteering");
      if (volStr) { try { setSavedVolunteering(JSON.parse(volStr) || []); } catch {} }

      // Interests
      const intStr = localStorage.getItem("interests");
      if (intStr) { try { setSavedInterests(JSON.parse(intStr) || []); } catch {} }

      // Websites
      const webStr = localStorage.getItem("websites");
      if (webStr) { try { setSavedWebsites(JSON.parse(webStr) || []); } catch {} }

      // Custom sections
      let tempCustom = {};
      if (userId) {
        const currentCustom = JSON.parse(localStorage.getItem(`current_custom_sections_${userId}`)) || [];
        currentCustom.forEach((name) => {
          const key = `custom_${userId}_${name}`;
          const str = localStorage.getItem(key);
          if (str) {
            try {
              const parsed = JSON.parse(str);
              const validItems = (parsed.items || []).filter(Boolean);
              if (validItems.length > 0) tempCustom[name] = validItems;
            } catch {}
          }
        });
      }
      setCustomSections(tempCustom);

      // Section order
      const savedOrder = localStorage.getItem("resume49SectionOrder");
      let finalOrder = savedOrder ? JSON.parse(savedOrder) : [...defaultSectionOrder];
      const currentCustomIds = Object.keys(tempCustom).map(n => `custom_${n}`);
      const cleaned = finalOrder.filter(s => !s.startsWith("custom_"));
      defaultSectionOrder.forEach(s => {
        if (!cleaned.includes(s)) cleaned.push(s);
      });
      finalOrder = [...cleaned, ...currentCustomIds];
      setSectionOrder(finalOrder);

      // Backend fetch
      if (token) {
        try {
          const contactRes = await axios.get("https://resume-maker-lc.onrender.com/api/contact/me", {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (contactRes.data) {
            setPersonalData(prev => ({
              ...prev,
              firstName:       contactRes.data.firstName       || prev.firstName,
              lastName:        contactRes.data.lastName        || prev.lastName,
              phone:           contactRes.data.phone           || prev.phone,
              email:           contactRes.data.email           || prev.email,
              city:            contactRes.data.city            || prev.city,
              country:         contactRes.data.country         || prev.country,
              pinCode:         contactRes.data.pinCode         || prev.pinCode,
              currentPosition: contactRes.data.currentPosition || prev.currentPosition,
              linkedIn:        contactRes.data.linkedIn        || prev.linkedIn,
              website:         contactRes.data.website         || prev.website,
              drivingLicence:  contactRes.data.drivingLicence  || prev.drivingLicence,
            }));
          }

          const workRes = await axios.get("https://resume-maker-lc.onrender.com/api/work/me", {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (workRes.data && Array.isArray(workRes.data)) setWorkExperiences(workRes.data);

          const eduRes = await axios.get("https://resume-maker-lc.onrender.com/api/education/me", {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (eduRes.data && Array.isArray(eduRes.data)) setEducationEntries(eduRes.data);

          const refRes = await axios.get("https://resume-maker-lc.onrender.com/api/references/me", {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (refRes.data && Array.isArray(refRes.data)) setSavedReferences(refRes.data);

        } catch (err) {
          console.log("Backend fetch error:", err);
        }
      }
    };

    loadInitialData();
  }, []);

  // ── Sync formData prop changes in real-time ────────────────────────────────
  useEffect(() => {
    if (formData && Object.keys(formData).length > 0) {
      setPersonalData(prev => ({
        ...prev,
        firstName:       formData.firstName       !== undefined ? formData.firstName       : prev.firstName,
        lastName:        formData.lastName        !== undefined ? formData.lastName        : prev.lastName,
        phone:           formData.phone           !== undefined ? formData.phone           : prev.phone,
        email:           formData.email           !== undefined ? formData.email           : prev.email,
        city:            formData.city            !== undefined ? formData.city            : prev.city,
        country:         formData.country         !== undefined ? formData.country         : prev.country,
        pinCode:         formData.pinCode         !== undefined ? formData.pinCode         : prev.pinCode,
        currentPosition: formData.currentPosition !== undefined ? formData.currentPosition : prev.currentPosition,
        linkedIn:        formData.linkedIn        !== undefined ? formData.linkedIn        : prev.linkedIn,
        website:         formData.website         !== undefined ? formData.website         : prev.website,
        drivingLicence:  formData.drivingLicence  !== undefined ? formData.drivingLicence  : prev.drivingLicence,
      }));
    }
  }, [formData]);

  // Save section order
  useEffect(() => {
    localStorage.setItem("resume49SectionOrder", JSON.stringify(sectionOrder));
  }, [sectionOrder]);

  // ── Drag & Drop handlers ───────────────────────────────────────────────
  const handleDragStart = (e, index) => {
    e.dataTransfer.setData("text/plain", JSON.stringify({ index, sectionId: sectionOrder[index] }));
    e.currentTarget.style.opacity = "0.5";
  };
  const handleDragEnd = (e) => { e.currentTarget.style.opacity = "1"; };
  const handleDragOver = (e, index) => { e.preventDefault(); setDragOverIndex(index); };
  const handleDrop = (e, targetIndex) => {
    e.preventDefault();
    const { index: source } = JSON.parse(e.dataTransfer.getData("text/plain"));
    if (source === targetIndex) return;
    const newOrder = [...sectionOrder];
    const [moved] = newOrder.splice(source, 1);
    newOrder.splice(targetIndex, 0, moved);
    setSectionOrder(newOrder);
    setDragOverIndex(null);
  };
  const resetSectionOrder = () => {
    setSectionOrder(defaultSectionOrder);
    localStorage.removeItem("resume49SectionOrder");
  };

  const ResumeHeader = () => (
    <Box className="resume-header-area" sx={{ textAlign: "center", mb: 3 }}>
      <Typography variant="h4" sx={{ fontWeight: 700, ...globalFontStyle, fontSize: `calc(${fontSize} * 1.5)` }}>
        {firstName.toUpperCase()} {lastName.toUpperCase()}
      </Typography>
      <Typography variant="h6" sx={{ color: "#444", ...globalFontStyle, mb: 2 }}>
        {personalData.currentPosition || ""}
      </Typography>

      {/* Contact row 1 */}
      <Box sx={{ display: "flex", justifyContent: "center", gap: 4, flexWrap: "wrap", mb: 2 }}>
        {personalData.phone && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <PhoneIcon fontSize="small" /> {personalData.phone}
          </Box>
        )}
        {personalData.email && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, wordBreak: "break-all" }}>
            <EmailIcon fontSize="small" /> {personalData.email}
          </Box>
        )}
        {personalData.city && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <LocationOnIcon fontSize="small" />
            {personalData.city}{personalData.country && `, ${personalData.country}`}
          </Box>
        )}
        {personalData.pinCode && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <LocationOnIcon fontSize="small" /> PIN: {personalData.pinCode}
          </Box>
        )}
      </Box>

      {/* Contact row 2 */}
      <Box sx={{ display: "flex", justifyContent: "center", gap: 4, flexWrap: "wrap", mb: 2 }}>
        {personalData.linkedIn && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <LinkedInIcon fontSize="small" sx={{ color: "#0077b5" }} />
            <a
              href={personalData.linkedIn.startsWith("http") ? personalData.linkedIn : `https://${personalData.linkedIn}`}
              target="_blank" rel="noopener noreferrer"
              style={{ color: "#0077b5", textDecoration: "none", ...globalFontStyle }}
            >
              {personalData.linkedIn.replace(/^https?:\/\/(www\.)?linkedin\.com\/in\/|linkedin\.com\/in\//, "")}
            </a>
          </Box>
        )}
        {personalData.website && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <LanguageIcon fontSize="small" sx={{ color: "#1976d2" }} />
            <a
              href={personalData.website.startsWith("http") ? personalData.website : `https://${personalData.website}`}
              target="_blank" rel="noopener noreferrer"
              style={{ color: "#1976d2", textDecoration: "none", wordBreak: "break-all", ...globalFontStyle }}
            >
              {personalData.website.replace(/^https?:\/\/(www\.)?/, "")}
            </a>
          </Box>
        )}
        {personalData.drivingLicence && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <DriveEtaIcon fontSize="small" sx={{ color: "#4caf50" }} />
            <span style={{ ...globalFontStyle }}>DL: {personalData.drivingLicence}</span>
          </Box>
        )}
      </Box>

      <Divider sx={{ borderColor: color, borderWidth: lineWeight, my: 3 }} />
    </Box>
  );

  const getSectionTitle = (id) => {
    const map = {
      summary:         "ABOUT ME",
      education:       "EDUCATION",
      experience:      "WORK EXPERIENCE",
      skills:          "SKILLS",
      languages:       "LANGUAGES",
      certifications:  "CERTIFICATIONS",
      accomplishments: "ACCOMPLISHMENTS",
      references:      "REFERENCES",
      softwareSkills:  "SOFTWARE SKILLS",
      volunteering:    "VOLUNTEERING",
      interests:       "INTERESTS",
      websites:        "WEBSITES / PROFILES",
      additionalInfo:  "ADDITIONAL INFORMATION",
    };
    if (id.startsWith("custom_")) {
      return id.replace("custom_", "").replace(/_/g, " ").toUpperCase();
    }
    return map[id] || id.toUpperCase();
  };

  // ── Section renderers ──────────────────────────────────────────────────
  const renderSectionContent = (sectionId) => {
    switch (sectionId) {

      case "summary":
        return savedSummary ? (
          <Box className="resume-section" sx={{ mb: `${sectionSpacing}px` }}>
            <Typography sx={sectionTitleStyle}>ABOUT ME</Typography>
            <Typography sx={{ lineHeight: 1.65, ...globalFontStyle, whiteSpace: "pre-line" }}>
              {savedSummary}
            </Typography>
          </Box>
        ) : null;

      case "education":
        return educationEntries.length > 0 ? (
          <Box className="resume-section" sx={{ mb: `${sectionSpacing}px` }}>
            <Typography sx={sectionTitleStyle}>EDUCATION</Typography>
            {educationEntries.map((edu, idx) => (
              <Box key={idx} sx={{ mb: `${paragraphSpacing * 1.5}px` }}>
                <Typography sx={{ fontWeight: 700, ...globalFontStyle, mb: `${paragraphSpacing / 2}px` }}>
                  {edu.schoolName || edu.collegeName || "Institution"} | {edu.gradYear || "Present"}
                </Typography>
                <Typography sx={{ fontWeight: 600, color: "#444", ...globalFontStyle, mb: `${paragraphSpacing / 2}px` }}>
                  {edu.degreeName || edu.degree || ""}
                </Typography>
                {edu.description && (
                  <Typography sx={{ ...globalFontStyle, whiteSpace: "pre-line", mb: `${paragraphSpacing / 2}px` }}>
                    {edu.description}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        ) : null;

      case "experience":
        return workExperiences.length > 0 ? (
          <Box className="resume-section" sx={{ mb: `${sectionSpacing}px` }}>
            <Typography sx={sectionTitleStyle}>WORK EXPERIENCE</Typography>
            {workExperiences.map((exp, idx) => (
              <Box key={idx} sx={{ mb: `${paragraphSpacing * 1.5}px` }}>
                <Typography sx={{ fontWeight: 700, ...globalFontStyle, mb: `${paragraphSpacing / 2}px` }}>
                  {exp.employer || exp.company || exp.companyName} | {exp.startYear || exp.startMonth || ""} - {exp.current || exp.currentlyWorking ? "Present" : (exp.endYear || exp.endMonth || "Present")}
                </Typography>
                <Typography sx={{ fontWeight: 600, color: "#444", ...globalFontStyle, mb: `${paragraphSpacing / 2}px` }}>
                  {exp.jobTitle || exp.position || exp.role}
                  {exp.location && ` | ${exp.location}`}
                </Typography>
                {exp.description && (
                  <Typography sx={{ ...globalFontStyle, whiteSpace: "pre-line", mb: `${paragraphSpacing / 2}px` }}>
                    {exp.description}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        ) : null;

      case "skills":
        return savedSkills.length > 0 ? (
          <Box className="resume-section" sx={{ mb: `${sectionSpacing}px` }}>
            <Typography sx={sectionTitleStyle}>SKILLS</Typography>
            <Grid container spacing={1} sx={{ pl: 1 }}>
              {savedSkills.map((s, i) => (
                <Grid item xs={4} key={i}>
                  <Typography sx={{ ...globalFontStyle, mb: `${paragraphSpacing}px` }}>
                    • {typeof s === "string" ? s : s.name || s.skillName || s.skill || ""}
                    {s.rating ? ` (${s.rating}%)` : ""}
                  </Typography>
                </Grid>
              ))}
            </Grid>
          </Box>
        ) : null;

      case "languages":
        return savedLanguages.length > 0 ? (
          <Box className="resume-section" sx={{ mb: `${sectionSpacing}px` }}>
            <Typography sx={sectionTitleStyle}>LANGUAGES</Typography>
            <Typography sx={{ ...globalFontStyle }}>
              {savedLanguages.map((l) =>
                typeof l === "string" ? l : l.name + (l.level ? ` (${l.level})` : "")
              ).join(", ")}
            </Typography>
          </Box>
        ) : null;

      case "certifications":
        return savedCertifications.length > 0 ? (
          <Box className="resume-section" sx={{ mb: `${sectionSpacing}px` }}>
            <Typography sx={sectionTitleStyle}>CERTIFICATIONS</Typography>
            {savedCertifications.map((c, i) => (
              <Typography key={i} sx={{ ...globalFontStyle, mb: `${paragraphSpacing}px` }}>
                • {c.name}{c.provider ? ` — ${c.provider}` : ""}{c.year ? ` (${c.year})` : ""}
              </Typography>
            ))}
          </Box>
        ) : null;

      case "accomplishments":
        return savedAccomplishments.length > 0 ? (
          <Box className="resume-section" sx={{ mb: `${sectionSpacing}px` }}>
            <Typography sx={sectionTitleStyle}>ACCOMPLISHMENTS</Typography>
            {savedAccomplishments.map((a, i) => (
              <Typography key={i} sx={{ ...globalFontStyle, mb: `${paragraphSpacing}px`, whiteSpace: "pre-line" }}>
                • {a}
              </Typography>
            ))}
          </Box>
        ) : null;

      case "references":
        return savedReferences.length > 0 ? (
          <Box className="resume-section" sx={{ mb: `${sectionSpacing}px` }}>
            <Typography sx={sectionTitleStyle}>REFERENCES</Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: "40px" }}>
              {savedReferences.map((ref, i) => {
                if (typeof ref === "string") {
                  return (
                    <Typography key={i} sx={{ ...globalFontStyle, whiteSpace: "pre-line", wordBreak: "break-word" }}>
                      {ref}
                    </Typography>
                  );
                }
                return (
                  <Box key={i} sx={{ wordBreak: "break-word", minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 700, ...globalFontStyle, mb: `${paragraphSpacing / 2}px` }}>
                      {ref.refName || ref.name || ref.referenceName}
                    </Typography>
                    <Typography sx={{ ...globalFontStyle, mb: `${paragraphSpacing / 2}px` }}>
                      {ref.company || ref.companyName}
                      {(ref.position || ref.jobTitle) && ` / ${ref.position || ref.jobTitle}`}
                    </Typography>
                    {ref.phone && <Typography sx={{ ...globalFontStyle, mb: `${paragraphSpacing / 2}px` }}>Phone: {ref.phone}</Typography>}
                    {ref.email && <Typography sx={{ ...globalFontStyle, mb: `${paragraphSpacing / 2}px` }}>Email: {ref.email}</Typography>}
                  </Box>
                );
              })}
            </Box>
          </Box>
        ) : null;

      case "softwareSkills":
        return softwareSkills.length > 0 ? (
          <Box className="resume-section" sx={{ mb: `${sectionSpacing}px` }}>
            <Typography sx={sectionTitleStyle}>SOFTWARE SKILLS</Typography>
            {softwareSkills.map((skill, i) => (
              <Typography key={i} sx={{ ...globalFontStyle, mb: `${paragraphSpacing}px` }}>
                • {skill.name} — {skill.level}%
              </Typography>
            ))}
          </Box>
        ) : null;

      case "volunteering":
        return savedVolunteering.length > 0 ? (
          <Box className="resume-section" sx={{ mb: `${sectionSpacing}px` }}>
            <Typography sx={sectionTitleStyle}>VOLUNTEERING</Typography>
            {savedVolunteering.map((vol, i) => (
              <Box key={i} sx={{ mb: `${paragraphSpacing}px` }}>
                <Typography sx={{ fontWeight: 700, ...globalFontStyle }}>
                  {vol.subtopic}
                </Typography>
                {(vol.fromDate || vol.toDate) && (
                  <Typography sx={{ ...globalFontStyle, fontStyle: "italic", color: "#555" }}>
                    {vol.fromDate} - {vol.toDate}
                  </Typography>
                )}
                {vol.content && (
                  <Typography sx={{ ...globalFontStyle, whiteSpace: "pre-line" }}>
                    {vol.content}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        ) : null;

      case "interests":
        return savedInterests.length > 0 ? (
          <Box className="resume-section" sx={{ mb: `${sectionSpacing}px` }}>
            <Typography sx={sectionTitleStyle}>INTERESTS</Typography>
            <Typography sx={{ ...globalFontStyle }}>
              {savedInterests.map(i => typeof i === "string" ? i : i.name || "").join(", ")}
            </Typography>
          </Box>
        ) : null;

      case "websites":
        return savedWebsites.length > 0 ? (
          <Box className="resume-section" sx={{ mb: `${sectionSpacing}px` }}>
            <Typography sx={sectionTitleStyle}>WEBSITES / PROFILES</Typography>
            {savedWebsites.map((site, i) => (
              <Typography key={i} sx={{ ...globalFontStyle, mb: `${paragraphSpacing}px`, wordBreak: "break-all" }}>
                • {site.url || site}
              </Typography>
            ))}
          </Box>
        ) : null;

      case "additionalInfo":
        return savedAdditionalInfo.length > 0 ? (
          <Box className="resume-section" sx={{ mb: `${sectionSpacing}px` }}>
            <Typography sx={sectionTitleStyle}>ADDITIONAL INFORMATION</Typography>
            {savedAdditionalInfo.map((info, i) => (
              <Typography key={i} sx={{ ...globalFontStyle, mb: `${paragraphSpacing}px`, whiteSpace: "pre-line", wordBreak: "break-word" }}>
                {info}
              </Typography>
            ))}
          </Box>
        ) : null;

      default:
        if (sectionId.startsWith("custom_")) {
          const name  = sectionId.replace("custom_", "");
          const items = customSections[name] || [];
          if (!items.length) return null;
          return (
            <Box className="resume-section" sx={{ mb: `${sectionSpacing}px` }}>
              <Typography sx={sectionTitleStyle}>
                {name.replace(/_/g, " ").toUpperCase()}
              </Typography>
              {items.map((item, i) => (
                <Typography key={i} sx={{ ...globalFontStyle, mb: `${paragraphSpacing}px`, wordBreak: "break-word" }}>
                  • {item}
                </Typography>
              ))}
            </Box>
          );
        }
        return null;
    }
  };

  const firstName = personalData.firstName || "";
  const lastName  = personalData.lastName  || "";

  const getPageSections = (pageIdx) => {
    if (totalPages === 1 || pageBreaks.length === 0) {
      return sectionOrder.filter(id => renderSectionContent(id) !== null);
    }

    const container = contentRef.current;
    if (!container) return sectionOrder.filter(id => renderSectionContent(id) !== null);

    const sections = container.querySelectorAll(".resume-section");
    const pageHeight = pagePx.height - topBottomMargin * 2;
    
    const headerEl = container.querySelector(".resume-header-area");
    const headerHeight = headerEl ? headerEl.getBoundingClientRect().height : 180;
    const dividerHeight = 20;
    const headerTotal = headerHeight + dividerHeight;

    let accumulated = 0;
    let currentIdx = 0;
    const pageChunks = [[]];
    const visibleSectionIds = sectionOrder.filter(id => renderSectionContent(id) !== null);

    sections.forEach((section, i) => {
      const h = section.getBoundingClientRect().height;
      const secId = visibleSectionIds[i];
      if (!secId) return;

      const currentLimit = pageHeight - (currentIdx === 0 ? headerTotal : 0);
      
      // Include bottom margin (sectionSpacing) in height calculation
      const totalHeight = h + (sectionSpacing || 0);
      
      if (accumulated + totalHeight > currentLimit && pageChunks[currentIdx].length > 0) {
        currentIdx++;
        pageChunks.push([]);
        accumulated = 0;
      }
      pageChunks[currentIdx].push(secId);
      accumulated += totalHeight;
    });

    return pageChunks[pageIdx] || [];
  };

  const getSectionsForCurrentPage = () => {
    return getPageSections(currentPage);
  };

  // Calculate totalPages and pageBreaks
  useEffect(() => {
    if (exporting) return;
    const container = contentRef.current;
    if (!container) return;

    const id = setTimeout(() => {
      const sections = Array.from(container.querySelectorAll(".resume-section"));
      const pageHeight = pagePx.height - (topBottomMargin) * 2;

      const headerEl = container.querySelector(".resume-header-area");
      const headerHeight = headerEl ? headerEl.getBoundingClientRect().height : 180;
      const dividerHeight = 20;
      const firstPageHeaderSpacing = headerHeight + dividerHeight;

      const breaks = [];
      let accumulated = 0;
      let pageIndex = 1;

      sections.forEach((section) => {
        const h = section.getBoundingClientRect().height;
        // Include section bottom spacing in height calculation
        const totalHeight = h + (sectionSpacing || 0);
        const currentLimit = pageHeight * pageIndex - (pageIndex === 1 ? firstPageHeaderSpacing : 0);
        
        if (accumulated + totalHeight > currentLimit) {
          breaks.push(pageIndex);
          pageIndex++;
          accumulated = 0; // reset for new page
        }
        accumulated += totalHeight;
      });

      const total = Math.max(1, pageIndex);
      setTotalPages(total);
      setPageBreaks(breaks);
    }, 400);

    return () => clearTimeout(id);
  }, [
    sectionOrder, savedSummary, workExperiences, educationEntries,
    savedSkills, savedLanguages, savedAccomplishments, savedCertifications,
    savedReferences, softwareSkills, savedVolunteering, savedInterests,
    savedWebsites, savedAdditionalInfo, customSections,
    topBottomMargin, pagePx.height, exporting,
  ]);

  // Clamp currentPage if totalPages shrinks
  useEffect(() => {
    if (currentPage > totalPages - 1) {
      setCurrentPage(Math.max(0, totalPages - 1));
    }
  }, [totalPages, currentPage]);

  return (
    <Box sx={{ width: "100%", position: "relative", bgcolor: '#f8f9fa', display: 'flex', flexDirection: 'column', alignItems: 'center', p: exporting ? 0 : 3 }}>
      {/* Navigation & Controls */}
      {!exporting && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 3, gap: 2, flexWrap: 'wrap' }}>
          <IconButton onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))} disabled={currentPage === 0} color="primary">
            <ChevronLeft />
          </IconButton>
          <Typography sx={{ mx: 2, ...globalFontStyle }}>
            Page {currentPage + 1} of {totalPages}
          </Typography>
          <IconButton onClick={() => setCurrentPage(prev => Math.min(totalPages - 1, prev + 1))} disabled={currentPage >= totalPages - 1} color="primary">
            <ChevronRight />
          </IconButton>

          {enableDragDrop && (
            <Button
              variant="contained"
              size="small"
              startIcon={<Reorder />}
              onClick={() => setArrangeDialogOpen(true)}
              sx={{ ml: 2 }}
            >
              Arrange Sections
            </Button>
          )}
        </Box>
      )}

      {/* Hidden measurement container */}
      <Box
        ref={contentRef}
        sx={{
          position: "absolute",
          visibility: "hidden",
          pointerEvents: "none",
          width: page.width,
          p: `${topBottomMargin}px ${sideMargins}px`,
          boxSizing: "border-box",
          left: -9999,
          zIndex: -1,
        }}
      >
        <ResumeHeader />
        {sectionOrder.map((sectionId) => {
          const content = renderSectionContent(sectionId);
          if (!content) return null;
          return (
            <React.Fragment key={sectionId}>
              {content}
            </React.Fragment>
          );
        })}
      </Box>

      {/* ── Page wrapper ── */}
      <Box id="resume-container">
        {exporting ? (
          Array.from({ length: totalPages }).map((_, pageIdx) => (
            <Paper
              key={pageIdx}
              className="resume-page"
              elevation={0}
              sx={{
                width: page.width,
                height: page.height,
                mx: "auto",
                bgcolor: "white",
                p: `${topBottomMargin}px ${sideMargins}px`,
                fontFamily: font,
                boxSizing: "border-box",
                overflow: "hidden",
                wordBreak: "break-word",
                overflowWrap: "break-word",
                mb: 3,
                pageBreakAfter: 'always',
                '@media print': {
                  width: page.width,
                  height: page.height,
                  p: `${topBottomMargin}px ${sideMargins}px`,
                  boxShadow: 'none',
                  margin: 0,
                  overflow: "hidden",
                  pageBreakAfter: 'always',
                }
              }}
            >
              {pageIdx === 0 && <ResumeHeader />}
              <Box
                sx={{
                  wordBreak: "break-word",
                  overflowWrap: "break-word",
                  overflow: "hidden",
                  width: "100%",
                  ...globalFontStyle
                }}
              >
                {getPageSections(pageIdx).map((sectionId) => {
                  const content = renderSectionContent(sectionId);
                  if (!content) return null;
                  return (
                    <React.Fragment key={sectionId}>
                      {content}
                    </React.Fragment>
                  );
                })}
              </Box>
            </Paper>
          ))
        ) : (
          <Paper
            className="resume-page"
            elevation={3}
            sx={{
              width: page.width,
              height: page.height,
              minHeight: page.height,
              maxHeight: page.height,
              mx: "auto",
              bgcolor: "white",
              p: `${topBottomMargin}px ${sideMargins}px`,
              fontFamily: font,
              boxSizing: "border-box",
              overflow: "hidden",
            }}
          >
            {currentPage === 0 && <ResumeHeader />}
            <Box
              sx={{
                wordBreak: "break-word",
                overflowWrap: "break-word",
                overflow: "hidden",
                width: "100%",
                ...globalFontStyle
              }}
            >
              {getSectionsForCurrentPage().map((sectionId) => {
                const content = renderSectionContent(sectionId);
                if (!content) return null;
                return (
                  <React.Fragment key={sectionId}>
                    {content}
                  </React.Fragment>
                );
              })}
            </Box>
          </Paper>
        )}
      </Box>

      {/* ── Drag & Drop Dialog ── */}
      <Dialog open={arrangeDialogOpen} onClose={() => setArrangeDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Arrange Resume Sections</DialogTitle>
        <DialogContent>
          <Box sx={{ maxHeight: 420, overflowY: "auto" }}>
            {sectionOrder.map((id, i) => (
              <Paper
                key={id}
                draggable
                onDragStart={(e) => handleDragStart(e, i)}
                onDragEnd={handleDragEnd}
                onDragOver={(e) => handleDragOver(e, i)}
                onDrop={(e) => handleDrop(e, i)}
                sx={{
                  p: 2,
                  mb: 1.5,
                  display: "flex",
                  alignItems: "center",
                  bgcolor:  dragOverIndex === i ? "#f0f8ff" : "white",
                  border:   dragOverIndex === i ? "2px dashed #1976d2" : "1px solid #ddd",
                  cursor: "grab",
                }}
              >
                <DragIndicator sx={{ mr: 2, color: "#888" }} />
                <Typography sx={{ flex: 1 }}>{getSectionTitle(id)}</Typography>
              </Paper>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button color="secondary" onClick={resetSectionOrder}>Reset</Button>
          <Button variant="contained" onClick={() => setArrangeDialogOpen(false)}>Done</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Resume49;